/* ================================================================================
   HumanArt — decorative inline-SVG illustrations for the portal's human-story layer.
   Clearly *art*, not photographs of real people — no patient data is represented.
   Decorative only: every piece is aria-hidden so assistive tech ignores it.
   Filled with the warm/human + trust-blue token palette so it stays on-brand.
   Hand-authored from simple shapes (circles for heads, curved strokes for arms/hands)
   rather than long opaque path dumps.
   ================================================================================ */

type ArtProps = {
  className?: string
  /** Which illustration to render. */
  variant?: 'joined-hands' | 'walking-together' | 'reaching-out'
}

/* Shared tokens (kept in sync with index.css ramps). */
const WARM = 'rgb(var(--warm-300))'
const WARM_DK = 'rgb(var(--warm-500))'
const HUMAN = 'rgb(var(--human-400))'
const HUMAN_DK = 'rgb(var(--human-600))'
const BLUE = 'rgb(var(--gov-blue-500))'
const BLUE_LT = 'rgb(var(--health-blue-400))'
const GREEN = 'rgb(var(--health-green-500))'

/**
 * Joined hands / community — three figures, arms reaching toward a shared centre.
 * The centre hands overlap to suggest mutual support. This is the homepage hero piece.
 */
function JoinedHands({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 320 240" className={className} fill="none" xmlns="http://www.w3.org/2000/svg" role="img" aria-hidden="true">
      {/* soft warm ground swell */}
      <ellipse cx="160" cy="212" rx="130" ry="20" fill={WARM} opacity="0.45" />
      {/* left figure */}
      <circle cx="74" cy="74" r="20" fill={BLUE} />
      <path d="M74 96 C 60 120, 60 150, 78 168 L 120 168 C 132 150, 128 120, 110 96 Z" fill={BLUE} opacity="0.9" />
      {/* right figure */}
      <circle cx="246" cy="74" r="20" fill={GREEN} />
      <path d="M246 96 C 260 120, 260 150, 242 168 L 200 168 C 188 150, 192 120, 210 96 Z" fill={GREEN} opacity="0.9" />
      {/* centre figure (taller, warm) — the person being supported */}
      <circle cx="160" cy="58" r="22" fill={HUMAN_DK} />
      <path d="M160 82 C 142 110, 142 150, 160 176 C 178 150, 178 110, 160 82 Z" fill={HUMAN} />
      {/* arms reaching in from both sides to meet the centre */}
      <path d="M92 132 C 118 128, 140 128, 158 132" stroke={BLUE_LT} strokeWidth="9" strokeLinecap="round" />
      <path d="M228 132 C 202 128, 180 128, 162 132" stroke={BLUE_LT} strokeWidth="9" strokeLinecap="round" />
      {/* joined hands at centre */}
      <circle cx="160" cy="132" r="13" fill={WARM_DK} />
      <circle cx="160" cy="132" r="6" fill={WARM} />
      {/* a small heart above the joined hands — human warmth */}
      <path
        d="M160 104 c -4 -5 -12 -2 -12 4 c 0 6 12 12 12 12 c 0 0 12 -6 12 -12 c 0 -6 -8 -9 -12 -4 Z"
        fill={HUMAN_DK}
      />
    </svg>
  )
}

/**
 * Walking together — two figures moving forward side by side (treatment journey / "you
 * are not alone"). Used on I Have TB / Get Help as a quieter human accent.
 */
function WalkingTogether({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 320 200" className={className} fill="none" xmlns="http://www.w3.org/2000/svg" role="img" aria-hidden="true">
      <ellipse cx="160" cy="176" rx="120" ry="16" fill={WARM} opacity="0.4" />
      {/* supporter (left) */}
      <circle cx="128" cy="56" r="18" fill={BLUE} />
      <path d="M128 76 C 116 98, 116 126, 124 150 L 132 170" stroke={BLUE} strokeWidth="12" strokeLinecap="round" />
      <path d="M124 150 C 112 150, 100 148, 92 142" stroke={BLUE} strokeWidth="9" strokeLinecap="round" />
      {/* patient (right, slightly leaning on supporter) */}
      <circle cx="188" cy="60" r="18" fill={HUMAN_DK} />
      <path d="M188 80 C 180 102, 180 130, 186 152 L 192 170" stroke={HUMAN} strokeWidth="12" strokeLinecap="round" />
      {/* linking arm between them */}
      <path d="M150 116 C 162 112, 174 114, 180 120" stroke={BLUE_LT} strokeWidth="9" strokeLinecap="round" />
      {/* forward path — a few dotted steps showing movement toward recovery */}
      <g fill={WARM_DK} opacity="0.6">
        <circle cx="64" cy="186" r="3" />
        <circle cx="86" cy="188" r="3" />
        <circle cx="108" cy="190" r="3" />
      </g>
      {/* sun-like warmth marker ahead */}
      <circle cx="262" cy="44" r="16" fill={HUMAN} opacity="0.5" />
    </svg>
  )
}

/**
 * Reaching out — a single open hand offering help. Used on Support a Patient.
 */
function ReachingOut({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 280 240" className={className} fill="none" xmlns="http://www.w3.org/2000/svg" role="img" aria-hidden="true">
      <ellipse cx="140" cy="214" rx="110" ry="16" fill={WARM} opacity="0.4" />
      {/* forearm + palm */}
      <path d="M40 150 L 120 150 L 120 120 L 200 120" stroke={HUMAN} strokeWidth="26" strokeLinecap="round" strokeLinejoin="round" />
      {/* fingers */}
      <g stroke={HUMAN} strokeWidth="12" strokeLinecap="round">
        <path d="M168 120 L 160 84" />
        <path d="M184 120 L 184 80" />
        <path d="M200 120 L 210 84" />
      </g>
      {/* thumb */}
      <path d="M150 132 L 128 116" stroke={HUMAN} strokeWidth="12" strokeLinecap="round" />
      {/* a small heart resting in the palm */}
      <path
        d="M150 108 c -5 -6 -15 -3 -15 5 c 0 8 15 16 15 16 c 0 0 15 -8 15 -16 c 0 -8 -10 -11 -15 -5 Z"
        fill={HUMAN_DK}
      />
      {/* trust-blue rays suggesting offering outward */}
      <g stroke={BLUE_LT} strokeWidth="4" strokeLinecap="round" opacity="0.6">
        <path d="M214 70 L 236 56" />
        <path d="M214 104 L 240 100" />
        <path d="M214 138 L 236 150" />
      </g>
    </svg>
  )
}

export function HumanArt({ className = '', variant = 'joined-hands' }: ArtProps) {
  const cls = className || 'w-full h-auto max-w-sm'
  switch (variant) {
    case 'walking-together':
      return <WalkingTogether className={cls} />
    case 'reaching-out':
      return <ReachingOut className={cls} />
    case 'joined-hands':
    default:
      return <JoinedHands className={cls} />
  }
}

export default HumanArt